/**
 * Smartspaza Marketplace — the shop buying stock.
 *
 * The layout is the supplied design: a search bar and supplier chips across the
 * top, a promotional band, a grid of product cards, and "My Order" down the
 * right. Two decisions in here are worth stating.
 *
 * **The basket panel is always present on a wide screen and stacks on a narrow
 * one.** A merchant adding six things wants to see the total move; hiding it
 * behind a cart icon turns "am I over budget" into a navigation.
 *
 * **Every bulk-priced card can be scheduled, not only the ones in a published
 * deal.** That is the whole of group buying from this screen: tapping "Buy with
 * other shops" puts the product in a pool with every other shop wanting the same
 * thing that week. The Group buys tab shows the campaigns a wholesaler has
 * published; the button on the card is what makes consolidation happen without
 * one.
 */

import { useState } from "react";

import { cn } from "@/lib/cn";

import { GroupBuys } from "./GroupBuys";
import { MarketplaceBasket } from "./MarketplaceBasket";
import { MarketplaceGrid } from "./MarketplaceGrid";
import { MarketplaceOrders } from "./MarketplaceOrders";

const TABS = [
  { id: "shop", label: "Shop" },
  { id: "group-buys", label: "Group buys" },
  { id: "orders", label: "My orders" },
] as const;

export function MarketplacePage() {
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("shop");

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <div
          role="tablist"
          aria-label="Marketplace sections"
          className="flex flex-wrap gap-1 border-b border-line"
        >
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={entry.id === tab}
              onClick={() => setTab(entry.id)}
              className={cn(
                "-mb-px h-touch border-b-2 px-4 text-sm font-medium transition-colors",
                entry.id === tab
                  ? "border-brand-500 text-ink-900"
                  : "border-transparent text-ink-500 hover:text-ink-900",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {tab === "shop" && <MarketplaceGrid />}
        {tab === "group-buys" && <GroupBuys />}
        {tab === "orders" && <MarketplaceOrders />}
      </div>

      {/* Stays on screen while browsing, and is not shown beside the order list
          — there it would be a second, competing list of the same products. */}
      {tab !== "orders" && (
        <aside className="w-full shrink-0 xl:w-80">
          <MarketplaceBasket />
        </aside>
      )}
    </div>
  );
}
